import { CustomFieldDefinitionActionAvailability } from '@/api/queries/customField/useGetFieldset';
import {
  CustomFieldDefinition,
  FieldsetCustomFieldDefinition,
  SelectCustomFieldDefinition,
  sortBySortOrder,
  UploadCustomFieldDefinition,
} from '@payaca/custom-fields';
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_FIELDSET_CHILDREN,
  MAX_IDENTIFIER_LENGTH,
  MAX_LABEL_LENGTH,
  MAX_UPLOAD_LIMIT,
  RESERVED_CUSTOM_FIELD_IDENTIFIERS,
} from '@payaca/custom-fields/constants/index';
import { INTERNAL_IDENTIFIER_VALIDATION_REGEX } from '@payaca/custom-fields/constants/regex';
import widen from '@payaca/utilities/widen';
import { z } from 'zod/v4';

const BASE_SCHEMA = z.object({
  identification: z.object({
    label: z.string().min(1).max(MAX_LABEL_LENGTH),
    identifier: z
      .string()
      .min(1)
      .max(MAX_IDENTIFIER_LENGTH)
      .regex(
        INTERNAL_IDENTIFIER_VALIDATION_REGEX,
        'Some characters are not allowed, for example: \\ / ? : * " < > | _ -'
      )
      .refine(
        (value) =>
          !widen<readonly string[]>(RESERVED_CUSTOM_FIELD_IDENTIFIERS).includes(
            value
          ),
        `Identifier must not be one of: ${RESERVED_CUSTOM_FIELD_IDENTIFIERS.join(', ')}`
      ),
  }),
  description: z.string().max(MAX_DESCRIPTION_LENGTH).optional().nullable(),
});

const SIMPLE_CUSTOM_FIELD_SCHEMA = BASE_SCHEMA.extend({
  type: z.enum([
    'textarea',
    'duration',
    'multi-upload',
    'text',
    'email',
    'url',
    'boolean',
    'number',
    'user',
  ]),
});

const SELECT_CUSTOM_FIELD_SCHEMA = BASE_SCHEMA.extend({
  type: z.literal('select'),
  options: z.array(z.string()).min(1),
  displayMode: z.enum(['dropdown', 'radio']),
});

const UPLOAD_CUSTOM_FIELD_SCHEMA = BASE_SCHEMA.extend({
  type: z.literal('upload'),
  // uploadType: z.literal('image')
  limit: z
    .number()
    .min(1)
    .max(MAX_UPLOAD_LIMIT, `Upload limit cannot exceed ${MAX_UPLOAD_LIMIT}`),
});

const uniqueIdentifiers = (
  items: { identification: { identifier: string } }[]
) => {
  const identifiers = items
    .map((item) => item.identification.identifier)
    .filter((x) => !!x);
  return new Set(identifiers).size === identifiers.length;
};

/**
 * Schema for leaf fields (non-fieldset fields).
 * These can exist at any depth within a fieldset hierarchy.
 */
export const LEAF_FIELD_SCHEMA = z.discriminatedUnion('type', [
  SIMPLE_CUSTOM_FIELD_SCHEMA,
  SELECT_CUSTOM_FIELD_SCHEMA,
  UPLOAD_CUSTOM_FIELD_SCHEMA,
]);

/**
 * Schema for nested fieldsets (depth 2).
 * These can only contain leaf fields, not other fieldsets.
 */
export const NESTED_FIELDSET_SCHEMA = BASE_SCHEMA.extend({
  type: z.literal('fieldset'),
  children: z
    .array(LEAF_FIELD_SCHEMA)
    .min(1)
    .max(MAX_FIELDSET_CHILDREN)
    .refine(uniqueIdentifiers, 'Field identifiers must be unique'),
});

/**
 * Schema for child fields at depth 1.
 * These can be either leaf fields or nested fieldsets.
 */
export const CHILD_FIELD_SCHEMA = z.discriminatedUnion('type', [
  SIMPLE_CUSTOM_FIELD_SCHEMA,
  SELECT_CUSTOM_FIELD_SCHEMA,
  UPLOAD_CUSTOM_FIELD_SCHEMA,
  NESTED_FIELDSET_SCHEMA,
]);

/**
 * Schema for root-level fieldsets (depth 1).
 * These can contain leaf fields or nested fieldsets.
 */
export const FIELDSET_CUSTOM_FIELD_SCHEMA = BASE_SCHEMA.extend({
  type: z.literal('fieldset'),
  children: z
    .array(CHILD_FIELD_SCHEMA)
    .min(1)
    .max(MAX_FIELDSET_CHILDREN)
    .refine(
      uniqueIdentifiers,
      'Custom field identifiers must be unique within a fieldset'
    ),
});

export type LeafFieldFormValue = z.infer<typeof LEAF_FIELD_SCHEMA>;
export type NestedFieldsetFormValue = z.infer<typeof NESTED_FIELDSET_SCHEMA>;
export type ChildFieldFormValue = z.infer<typeof CHILD_FIELD_SCHEMA>;
export type FieldsetFormValue = z.infer<typeof FIELDSET_CUSTOM_FIELD_SCHEMA>;

/**
 * Helper to validate that upload limits are not reduced for fields in use.
 * Adds a Zod issue if the field is an upload type, has an existing definition,
 * and the new limit is lower than the existing limit.
 */
const validateUploadLimitNotReduced = (
  ctx: z.RefinementCtx,
  newField: LeafFieldFormValue,
  existingField: CustomFieldDefinition | undefined,
  fieldPath: string,
  actionAvailability: CustomFieldDefinitionActionAvailability[],
  errorPath: (string | number)[]
) => {
  if (newField.type !== 'upload') return;
  if (!existingField || existingField.type !== 'upload') return;

  const action = actionAvailability.find((a) => a.fieldPath === fieldPath);
  if (!action) return;
  if (action.isDestructivelyUpdateable.result) return;

  const existingUploadField = existingField as UploadCustomFieldDefinition;
  if (newField.limit < existingUploadField.limit) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'Cannot reduce the upload limit for this field because it is in use',
      path: errorPath,
    });
  }
};

/**
 * Builds a dynamic validation schema for a fieldset's children based on the existing fieldset definition and action availability.
 * e.g. Doesn't allow destructive updates to upload limits if not permitted.
 */
export const buildDynamicValidationSchema = (
  existingFieldset?: FieldsetCustomFieldDefinition,
  actionAvailability: CustomFieldDefinitionActionAvailability[] = []
) => {
  if (!existingFieldset) {
    return FIELDSET_CUSTOM_FIELD_SCHEMA;
  }

  const fieldsetIdentifier = existingFieldset.identifier;

  // Schema for depth-1 children (can be leaf fields or nested fieldsets)
  const dynamicChildSchema = z.lazy(() =>
    CHILD_FIELD_SCHEMA.superRefine((value, ctx) => {
      const fieldPath = `${fieldsetIdentifier}.${value.identification.identifier}`;

      if (value.type === 'fieldset') {
        // Nested fieldset - validate its children
        const existingNestedFieldset = existingFieldset.children.find(
          (c) =>
            c.identifier === value.identification.identifier &&
            c.type === 'fieldset'
        ) as FieldsetCustomFieldDefinition | undefined;

        if (existingNestedFieldset) {
          value.children.forEach((nestedChild, index) => {
            const nestedPath = `${fieldPath}.${nestedChild.identification.identifier}`;
            const existingNestedChild = existingNestedFieldset.children.find(
              (c) => c.identifier === nestedChild.identification.identifier
            );

            validateUploadLimitNotReduced(
              ctx,
              nestedChild,
              existingNestedChild,
              nestedPath,
              actionAvailability,
              ['children', index, 'limit']
            );
          });
        }
      } else {
        // Leaf field - validate upload limit
        const existingChild = existingFieldset.children.find(
          (c) => c.identifier === value.identification.identifier
        );

        validateUploadLimitNotReduced(
          ctx,
          value,
          existingChild,
          fieldPath,
          actionAvailability,
          ['limit']
        );
      }
    })
  );

  return FIELDSET_CUSTOM_FIELD_SCHEMA.extend({
    children: z
      .array(dynamicChildSchema)
      .min(1)
      .max(MAX_FIELDSET_CHILDREN)
      .refine(uniqueIdentifiers, 'Identifiers must be unique'),
  });
};

const buildDefaultValuesForLeafField = (
  fieldDefinition: Exclude<CustomFieldDefinition, FieldsetCustomFieldDefinition>
): LeafFieldFormValue => {
  const baseValues = {
    type: fieldDefinition.type,
    identification: {
      label: fieldDefinition.label,
      identifier: fieldDefinition.identifier,
    },
    description: fieldDefinition.description,
  };

  if (fieldDefinition instanceof SelectCustomFieldDefinition) {
    return {
      ...baseValues,
      type: 'select' as const,
      options: fieldDefinition.options.filter((o) => o) as string[],
      displayMode: fieldDefinition.displayMode,
    };
  }

  if (fieldDefinition instanceof UploadCustomFieldDefinition) {
    return {
      ...baseValues,
      type: 'upload' as const,
      limit: fieldDefinition.limit,
    };
  }

  // Simple types: text, textarea, email, url, boolean, number, duration, multi-upload, user
  return baseValues as z.infer<typeof SIMPLE_CUSTOM_FIELD_SCHEMA>;
};

const buildDefaultValuesForChildField = (
  fieldDefinition: CustomFieldDefinition
): ChildFieldFormValue => {
  if (fieldDefinition instanceof FieldsetCustomFieldDefinition) {
    return {
      type: 'fieldset' as const,
      identification: {
        label: fieldDefinition.label,
        identifier: fieldDefinition.identifier,
      },
      description: fieldDefinition.description,
      children: fieldDefinition.children
        .toSorted((a, b) => sortBySortOrder(a.schema, b.schema))
        .map(buildDefaultValuesForLeafField),
    };
  }
  return buildDefaultValuesForLeafField(fieldDefinition);
};

export const buildDefaultValuesForFieldset = (
  fieldsetDefinition: FieldsetCustomFieldDefinition
): FieldsetFormValue => {
  return {
    type: fieldsetDefinition.type,
    identification: {
      label: fieldsetDefinition.label,
      identifier: fieldsetDefinition.identifier,
    },
    description: fieldsetDefinition.description,
    children: fieldsetDefinition.children
      .toSorted((a, b) => sortBySortOrder(a.schema, b.schema))
      .map(buildDefaultValuesForChildField),
  };
};
